// ============================================
// API Response Types
// ============================================

export interface ThirdPartiesResponse {
  success: boolean;
  third_parties: ThirdParty[];
  total_third_parties: number;
  total_active_assessments: number;
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface ThirdParty {
  vendor_name: string;
  risk_level: RiskLevel | null;
  last_updated_at: string | null;
  assessments_needs_review_count: number;
  number_of_assessments: number;
  vendor_logo_url: string | null;
  vendor_status: VendorStatus | null;
  vendor_business_criticality?: BusinessCriticality | null;
  assessments: (Assessment | NewRequest)[];
}

export interface NewRequest {
  externally_filled_intake_form_id: string;
  vendor_name: string;
  product_name: string | null;
  business_owner_email: string;
  tier: number | null;
  submitted_at: string;
  created_at: string;
  is_pending_intake_request: true;
}

export interface Assessment {
  type: "multi_file_vendor_assessment";
  name: string;
  use_training_data: boolean;
  customize: boolean;
  is_active: boolean;
  multi_file_vendor_assessment: MultiFileVendorAssessment;
  sec_ctx: null;
  organization_id: string;
  tag: string;
  status: AssessmentStatus;
  risk_level: RiskLevel | null;
  business_criticality?: BusinessCriticality | null;
  assessment_step: number;
  total_assessment_steps: number;
  created_by: string;
  permissions: AssessmentPermissions;
  deleted: boolean;
  final_report_available: boolean;
  last_reassessed_by: string | null;
  last_reassessed_at: string | null;
  id: string;
  created_at: string;
  updated_at: string;
  version: number;
  archived_at: string | null;
  archived_by: string | null;
  externally_filled_intake_form_id?: string | null;
}

export interface MultiFileVendorAssessment {
  vendor_name: string;
  product_name: string | null;
  vendor_logo_url: string | null;
  vendor_domain_url: string | null;
  scope_of_integration: string | null;
  vendor_email: string | null;
  response_quality: ResponseQuality;
  doc_type: DocType;
  vendor_filled_intake_form: boolean;
  questionnaire_id: string | null;
  questionnaire_url: null;
  contact_email: string | null;
  selected_control_sets: string[];
  create_new_control_set: boolean;
  risk_level: RiskLevel | null;
  risk_rating_factors: RiskRatingFactors | null;
}

export interface AssessmentPermissions {
  can_delete: boolean;
  can_reassess: boolean;
  can_view: boolean;
  can_edit: boolean;
}

export interface RiskRatingFactors {
  data_sensitivity: string | null;
  business_criticality: string | null;
  integration_depth: string | null;
}

export type ResponseQuality = "high" | "medium" | "low" | null;

export type DocType = "security_questionnaire" | "soc2_report" | "other" | null;

// ============================================
// Enums & Literals
// ============================================

export type RiskLevel = "Low" | "Medium" | "High";

export type BusinessCriticality = "Low" | "Medium" | "High" | "Critical";

export type VendorStatus = "in_progress" | "complete" | "needs_review";

export type AssessmentStatus =
  | "in_progress"
  | "reassessment_in_progress"
  | "input_needed"
  | "form_sent"
  | "complete"
  | "needs_review"
  | "new_request";

export function isAssessment(item: Assessment | NewRequest): item is Assessment {
  return !("is_pending_intake_request" in item && item.is_pending_intake_request);
}

export function isNewRequest(item: Assessment | NewRequest): item is NewRequest {
  return "is_pending_intake_request" in item && item.is_pending_intake_request === true;
}

export type FilterKey = "all_risks" | "high" | "medium" | "low";

export type AssessmentStatusFilter =
  | "intake_form"
  | "information_gathering"
  | "generating_results"
  | "needs_review"
  | "complete"
  | "continuous_monitoring";

export type BusinessCriticalityFilter = "low" | "medium" | "high" | "critical";

export type SortField = "last_updated_at" | "vendor_name" | "risk_level" | "status";

export type SortOrder = "asc" | "desc";

export type ActionType = "view_assessment" | "download_report" | "download_summary" | "reassess" | "version_history";

// ============================================
// Query Parameter Types
// ============================================

export interface VendorsQueryParams {
  search_key?: string;
  filter_keys?: FilterKey[];
  assessment_status?: AssessmentStatusFilter;
  business_criticality?: BusinessCriticalityFilter[];
  page?: number;
  page_size?: number;
  sort_by?: SortField;
  sort_order?: SortOrder;
}

// ============================================
// Component Props Types
// ============================================

export interface VendorRowProps {
  vendor: ThirdParty;
  isExpanded: boolean;
  onToggle: () => void;
}

export interface ProductRowProps {
  assessment: Assessment;
  vendorName: string;
  onAction: (action: ActionType, assessment: Assessment) => void;
}

// ============================================
// Version History Types
// ============================================

export interface AssessmentHistoryResponse {
  success: boolean;
  vendor_name: string;
  product_name: string | null;
  history: Assessment[];
}

export interface AssessmentHistoryParams {
  vendor_name: string;
  product_name?: string;
}

export type VersionStatus = "active" | "archived" | "completed";
